import { auth, workoutExecution, workoutPlans } from '../../services/api';
import { toMyPlanRequest } from './MyPlanGeneratorDialog';

/*
 * «Gerar o treino de hoje» — a parte que não é ecrã.
 *
 * O hub do Treino (separador Hoje) e o diálogo «Treino de hoje» usam ESTAS funções: o mesmo pedido ao motor,
 * a mesma leitura do que já se treinou hoje e a mesma gravação no nome do atleta. Um só sítio para o caminho
 * que já existe — duas versões do mesmo pedido acabariam por divergir.
 *
 * O motor de planos é o que existe e não se inventam regras novas dele: só aceita propostas de 2 a 6 dias por
 * semana (`WorkoutPlanGenerator.cs`: «Os dias por semana têm de estar entre 2 e 6»), por isso pede-se sempre a
 * proposta mais curta — 2 dias — e treina-se HOJE a primeira sessão dela. Nada é gravado antes de o atleta o
 * pedir, e sem histórico não há cargas: o gerador não as inventa e nenhum ecrã escreve nenhuma.
 */

/** O mínimo que o motor aceita (o gerador recusa menos de 2 dias por semana) — a proposta mais curta é esta. */
export const TODAY_DAYS_PER_WEEK = 2;

/** Os tempos que o motor aceita (30 a 120 minutos) e que o atleta escolhe no próprio ecrã do treino de hoje. */
export const TODAY_MINUTE_OPTIONS = [30, 45, 60, 75, 90, 120];

/** Com que objetivo e experiência se pede o treino de hoje — os mesmos com que o diálogo o pede. */
export const TODAY_WORKOUT_DEFAULTS = { goal: 'hypertrophy', experience: 'beginner' };

/** A primeira sessão do plano que tem exercícios (a que se pode treinar hoje), e onde está. */
export const firstPlayableDay = (days) => {
  const index = (days || []).findIndex((d) => (d.exercises || []).length > 0);
  return { index, day: index >= 0 ? days[index] : null };
};

/** O que ele já treinou HOJE (dias desde o último treino = 0) — um facto do histórico, não um limiar inventado. */
export const trainedTodayMuscles = (recovery) => (recovery?.muscles || []).filter((m) => m.muscleId && m.daysSince === 0);

/** O que já se treinou hoje, como o servidor o sabe: { state: 'ok'|'error', muscles }. Em falha, nenhum músculo de fora. */
export const fetchTodayRecovery = () => workoutExecution.muscleRecovery()
  .then(({ data }) => ({ state: 'ok', muscles: data?.muscles || [] }))
  .catch(() => ({ state: 'error', muscles: [] }));

/** Pede ao motor a proposta de hoje para o tempo escolhido. Lança quando o servidor recusa (o ecrã mostra o erro dele). */
export const generateTodayProposal = async ({ sessionMinutes, goal, experience, seed = 0, avoidMuscles = [] }) => {
  const { data } = await workoutPlans.generateMine({
    goal,
    experience,
    daysPerWeek: TODAY_DAYS_PER_WEEK,
    sessionMinutes: Number(sessionMinutes),
    avoidMuscles,
    seed,
  });
  return data;
};

/**
 * Grava a proposta no nome do atleta e devolve o dia a treinar HOJE (o que o SERVIDOR devolveu — nunca se
 * começa um dia à sorte). Lança quando é o servidor a recusar a gravação: o chamador mostra o erro dele e
 * deixa repetir. Nos dois casos que o ecrã tem de explicar por palavras devolve { ok: false, message }.
 */
export const saveTodayWorkout = async (plan) => {
  // De quem é o plano: quem o SERVIDOR diz que está autenticado. Sem isso não se grava nada em nome de ninguém.
  let userId;
  try {
    const { data: me } = await auth.me();
    userId = me?.id;
  } catch {
    userId = undefined;
  }
  if (!userId) return { ok: false, message: 'Não foi possível confirmar a sua sessão. Volte a entrar e tente outra vez.' };

  const { index, day: chosen } = firstPlayableDay(plan?.days || []);
  const { data } = await workoutPlans.create(toMyPlanRequest(plan, userId));
  const created = data?.workoutDays || [];
  // O dia é o MESMO que se mostrou; se o servidor não o devolver, não se começa um dia à sorte.
  const day = created.find((d) => d.dayName === chosen?.name) || created[index];
  if (!day?.id) {
    return { ok: false, saved: true, message: 'O treino ficou guardado, mas o servidor não devolveu o dia para começar agora. Abra o separador Plano e comece-o por lá.' };
  }
  return { ok: true, dayId: day.id, dayName: day.dayName, userId };
};
